import SiteFooter from "../components/SiteFooter";
import { Link } from "react-router-dom";
import { ArrowRight, Star, ShieldCheck, Map, Mail, Phone, Sparkles, CheckCircle2, AlertCircle } from "lucide-react";
import Brand from "../components/Brand";

/* /dashboard/critical-tools
   Mike's "must-have" affiliate tool stack for seniors. Each tool has a free
   tier big enough that a new member can learn the ropes WITHOUT spending a
   dime. These are the partners we actively recommend — not just listed.

   Add new critical tools to the TOOLS array below. Anything with a
   strong free tier + cash affiliate program belongs here. */

const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const WINE  = "#0F172A";
const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const CREAM = "#FFFFFF";

const TOOLS = [
  // Future critical tools land here. Apify removed — they declined our affiliate
  // request and we are not pursuing lead-scraping. New partners with strong free
  // tiers + affiliate programs belong here.
];

export default function CriticalToolsPage() {
  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden border-b-4"
               style={{ background: `linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)`, borderColor: GREEN }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <Link to="/dashboard" className="text-[11px] tracking-[0.3em] uppercase font-bold hover:opacity-70 transition"
                style={{ color: WINE }} data-testid="critical-tools-back-btn">
            ← My Office
          </Link>

          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mt-5 mb-2"
               style={{ color: GREEN }}>★ OUTSIDE TOOLS WE USE</div>
          <h1 className="text-5xl md:text-6xl uppercase leading-[0.95]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: WINE, textWrap: "balance" }}>
            Tools We Recommend<span style={{ color: ORANGE }}>.</span>
          </h1>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="rounded-xl p-4 border-2 bg-white" style={{ borderColor: GREEN }}>
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} />
                <div className="text-[11px] tracking-wider uppercase font-bold" style={{ color: GREEN }}>Outside Tools We Use</div>
              </div>
              <p className="text-sm" style={{ color: "#3D3D3D" }}>
                A short list of outside services we actually use. Honest descriptions — no hype, no fake "must-haves." If we get approved as their affiliate, we'll add a referral link. Until then, this is just what we use.
              </p>
            </div>
            <div className="rounded-xl p-4 border-2 bg-white" style={{ borderColor: ORANGE }}>
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4" style={{ color: ORANGE }} />
                <div className="text-[11px] tracking-wider uppercase font-bold" style={{ color: ORANGE }}>Products In The Market</div>
              </div>
              <p className="text-sm" style={{ color: "#3D3D3D" }}>
                Books, supplements, and other products you can promote for commission live in the <Link to="/market" className="underline font-bold" style={{ color: GREEN }}>Market</Link>. Different page, different deal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TOOLS LIST ═══ */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10 md:py-14 space-y-6">
        {TOOLS.length === 0 && (
          <div data-testid="critical-tools-empty"
               className="rounded-2xl border-2 bg-white shadow-md p-8 md:p-10 text-center"
               style={{ borderColor: GREEN }}>
            <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: ORANGE }} />
            <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: WINE }}>
              New Tool Recommendations Coming Soon
            </h2>
            <p className="text-base" style={{ color: "#3D3D3D" }}>
              We're vetting new outside services for our members. When we approve one, it lands here with an honest review and a free-tier rundown.
            </p>
          </div>
        )}
        {TOOLS.map((t) => (
          <article key={t.slug}
                   data-testid={`critical-tool-${t.slug}`}
                   className="rounded-2xl border-2 bg-white shadow-md hover:shadow-xl transition overflow-hidden"
                   style={{ borderColor: GOLD }}>
            <div className="grid md:grid-cols-[80px_1fr] gap-0">
              {/* Left badge */}
              <div className="flex md:flex-col items-center justify-center gap-2 p-4 md:p-5"
                   style={{ background: "#FFFFFF", color: GOLD }}>
                <Star className="w-6 h-6" />
                <div className="text-[9px] tracking-[0.18em] uppercase font-bold text-center leading-tight">
                  CRITICAL<br/>TOOL
                </div>
              </div>

              {/* Right content */}
              <div className="p-5 md:p-7">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <t.Icon className="w-5 h-5" style={{ color: GREEN }} />
                      <h2 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight" style={{ color: NAVY, fontWeight: 700 }}>
                        {t.name}
                      </h2>
                    </div>
                    <p className="text-base italic" style={{ color: WINE }}>{t.tagline}</p>
                  </div>
                  {t.status === "soon" && (
                    <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-3 py-1 rounded-full"
                          style={{ background: "#F4E5C4", color: WINE }}>Coming Soon</span>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-5">
                  <div className="rounded-lg border p-3" style={{ borderColor: "#BAE6FD", background: "#FFFFFF" }}>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: GREEN }}>
                      ★ The Free Tier
                    </div>
                    <div className="text-sm font-bold" style={{ color: NAVY }}>{t.free}</div>
                  </div>
                  <div className="rounded-lg border p-3" style={{ borderColor: "#E5E1D6" }}>
                    <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#7A7066" }}>
                      If You Outgrow Free
                    </div>
                    <div className="text-sm" style={{ color: "#3D3D3D" }}>{t.paid}</div>
                  </div>
                </div>

                <div className="mt-4 text-sm" style={{ color: "#3D3D3D" }}>
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>
                    Best For
                  </div>
                  {t.bestFor}
                </div>

                <div className="mt-4 rounded-lg p-3" style={{ background: "#FFFFFF", border: `1px solid ${GOLD}55` }}>
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: WINE }}>
                    Why It's Critical
                  </div>
                  <div className="text-sm" style={{ color: "#3D3D3D" }}>{t.whyCritical}</div>
                </div>

                <div className="mt-5">
                  {t.external ? (
                    <a href={t.link} target="_blank" rel="noopener noreferrer"
                       data-testid={`critical-tool-cta-${t.slug}`}
                       className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow hover:scale-[1.03] transition"
                       style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
                      Start Free <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : (
                    <Link to={t.link}
                          data-testid={`critical-tool-cta-${t.slug}`}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold uppercase tracking-wider shadow hover:scale-[1.03] transition"
                          style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
                      See How It Works <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>

      <SiteFooter />
    </div>
  );
}
